import path from 'node:path';
import { useStore } from '@nanostores/react';
import { memo, useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';
import { toast } from 'react-toastify';
import { FileContextMenu, type ClipboardSelection, type FileChangeCallback } from './FileContextMenu';
import { FileOrFolder } from './FileOrFolder';
import { compareFiles } from './file-tree/sort-files';
import { FileSizeToast } from '~/components/toasts/file-size';
import { getFileSizeLimit } from '~/lib/file-limits';
import { useDragDrop } from '~/lib/hooks';
import { useCollapsedFolders } from '~/lib/hooks/useCollapsedFolders';
import { type FileMap } from '~/lib/stores/files';
import { userStore } from '~/lib/stores/user';
import { workbenchStore } from '~/lib/stores/workbench';
import type { Node } from '~/types/file-tree';
import { classNames } from '~/utils/classNames';
import { WORK_DIR } from '~/utils/constants';
import { getUint8ArrayFromFile } from '~/utils/file-uint8array';
import { isBinaryFile } from '~/utils/is-binary-file';
import { renderLogger } from '~/utils/logger';
import { logger } from '~/utils/logger';
import { removeWorkDir } from '~/utils/workDir';

const DEFAULT_HIDDEN_FILES = [
  /\/node_modules\//,
  /\/\.next/,
  /\/\.nuxt/,
  /\/\.astro/,
  /\/\.contentlayer/,
  /\/\.svelte-kit/,
  /\/\.vitepress/,
  /\/\.vscode/,
  /\/\.stackblitz/,
  /\/\.codesandbox/,
];

interface Props {
  files?: FileMap;
  selectedFile?: string;
  onFileSelect?: (filePath: string) => void;
  onFileChange?: FileChangeCallback;
  rootFolder?: string;
  hideRoot?: boolean;
  collapsed?: boolean;
  preserveCollapsedState?: boolean;
  allowFolderSelection?: boolean;
  hiddenFiles?: Array<string | RegExp>;
  className?: string;
  provideQuickActions?: (fileOrFolder: Node) => ReactNode;
  hasDragDrop?: boolean;
}

export const FileTree = memo(
  ({
    files = {},
    onFileSelect,
    onFileChange,
    selectedFile,
    rootFolder,
    hideRoot = false,
    collapsed = false,
    preserveCollapsedState = false,
    allowFolderSelection = false,
    hiddenFiles,
    className,
    provideQuickActions,
    hasDragDrop = false,
  }: Props) => {
    renderLogger.trace('FileTree');

    const [isOverDropZone, setIsOverDropZone] = useState(false);

    const [dropPath, setDropPath] = useState<string | undefined>(undefined);

    const tier = useStore(userStore.getTierStore());
    const isPaidUser = useStore(userStore.isPaidUser);

    const { isDragging, handleDrop } = useDragDrop((file) => {
      setDropPath(undefined);
      setIsOverDropZone(false);

      const fileSizeLimit = getFileSizeLimit({ tier, target: 'bolt' });

      if (file.size > fileSizeLimit.bytes) {
        toast.error(() => <FileSizeToast limit={fileSizeLimit.mb} isPaidUser={isPaidUser} />);

        return;
      }

      const path = dropPath ? `${removeWorkDir(dropPath)}/${file.name}` : file.name;
      const uniquePath = workbenchStore.generateUniqueName(path, 'file');

      getUint8ArrayFromFile(file)
        .then((content) => {
          return workbenchStore.writeFile(uniquePath, {
            type: 'file',
            content,
            isBinary: isBinaryFile(content),
          });
        })
        .catch((error) => {
          toast.error('Failed to add file');
          logger.error(error.message);
        });
    });

    const handleDragEnter = (node: Node) => {
      if (!hasDragDrop) {
        return;
      }

      const fullPath = node.kind === 'file' ? path.dirname(node.fullPath) : node.fullPath;

      if (fullPath !== WORK_DIR) {
        setDropPath(fullPath);
      }
    };

    const handleDragLeave = (node: Node) => {
      if (!hasDragDrop) {
        return;
      }

      const fullPath = node.kind === 'file' ? path.dirname(node.fullPath) : node.fullPath;

      if (dropPath === fullPath) {
        setDropPath(undefined);
      }
    };

    const handleNodeDrop = (node: Node) => (event: React.DragEvent<HTMLDivElement>) => {
      if (!hasDragDrop) {
        return;
      }

      event.stopPropagation();

      handleDrop(event);

      if (node.kind === 'folder') {
        if (collapsedFolders.has(node.fullPath)) {
          toggleCollapseState(node.fullPath);
        }
      }
    };

    const [clipboardSelection, setClipboard] = useState<ClipboardSelection>();
    const computedHiddenFiles = useMemo(() => [...DEFAULT_HIDDEN_FILES, ...(hiddenFiles ?? [])], [hiddenFiles]);

    const fileList = useMemo(() => {
      return buildFileList(files, rootFolder, hideRoot, computedHiddenFiles);
    }, [files, rootFolder, hideRoot, computedHiddenFiles]);

    const { collapsedFolders, toggleCollapseState, filteredFileList } = useCollapsedFolders(
      fileList,
      collapsed,
      selectedFile,
      preserveCollapsedState,
    );

    function onKeyUp(event: KeyboardEvent<HTMLDivElement>) {
      if (event.key === 'Escape') {
        setClipboard(undefined);
      }
    }

    function isSelected(node: Node) {
      if (hasDragDrop && isOverDropZone) {
        return node.fullPath === dropPath;
      }

      return node.kind === 'folder'
        ? allowFolderSelection && selectedFile === node.fullPath
        : selectedFile === node.fullPath;
    }

    return (
      <div
        className={classNames(
          'text-sm flex flex-col',
          {
            'bg-bolt-elements-filetree-drop-background': hasDragDrop && isDragging,
            'outline outline-bolt-elements-borderColorActive': hasDragDrop && isOverDropZone && !dropPath,
          },
          className,
        )}
        onDragOver={() => setIsOverDropZone(true)}
        onDragLeave={() => setIsOverDropZone(false)}
        onDrop={handleDrop}
        onKeyUp={onKeyUp}
      >
        {filteredFileList.map((node) => (
          <FileTreeItem
            key={node.id}
            node={node}
            isSelected={isSelected(node)}
            isCollapsed={node.kind === 'folder' && collapsedFolders.has(node.fullPath)}
            onClick={() => (node.kind === 'file' ? onFileSelect?.(node.fullPath) : toggleCollapseState(node.fullPath))}
            onFileChange={onFileChange}
            onExpand={() => toggleCollapseState(node.fullPath)}
            setClipboard={setClipboard}
            clipboardSelection={clipboardSelection}
            provideQuickActions={provideQuickActions}
            onDragOver={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleNodeDrop(node)}
          />
        ))}

        <FileContextMenu
          position="after"
          onFileChange={onFileChange}
          onClipboard={setClipboard}
          clipboardSelection={clipboardSelection}
          triggerProps={{ className: 'grow-1 outline-0', onKeyUp, tabIndex: 0 }}
        />
      </div>
    );
  },
);

function FileTreeItem({
  node,
  isSelected,
  isCollapsed,
  onClick,
  onFileChange,
  onExpand,
  setClipboard,
  clipboardSelection,
  provideQuickActions,
  onDrop,
  onDragOver,
  onDragLeave,
}: {
  node: Node;
  isSelected: boolean;
  isCollapsed: boolean;
  onClick: () => void;
  onFileChange: Props['onFileChange'];
  onExpand: () => void;
  setClipboard: (value?: ClipboardSelection) => void;
  clipboardSelection?: ClipboardSelection;
  provideQuickActions: Props['provideQuickActions'];
  onDrop?: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragOver?: (node: Node) => void;
  onDragLeave?: (node: Node) => void;
}) {
  return (
    <FileContextMenu
      node={node}
      nodeIsSelected={isSelected}
      onFileChange={onFileChange}
      clipboardSelection={clipboardSelection}
      onClipboard={setClipboard}
      nodeIsCollapsed={isCollapsed}
      onExpandNode={() => isCollapsed && onExpand()}
    >
      <div
        onDragOver={() => {
          onDragOver?.(node);
        }}
        onDragLeave={() => {
          onDragLeave?.(node);
        }}
        onDrop={onDrop}
      >
        <FileOrFolder
          node={node}
          selected={isSelected}
          provideQuickActions={provideQuickActions}
          isSelectedAsCut={clipboardSelection?.type === 'cut' && clipboardSelection?.name === node.fullPath}
          collapsed={isCollapsed}
          onClick={onClick}
          parentPath={node.fullPath.split('/').slice(0, -1).join('/')}
        />
      </div>
    </FileContextMenu>
  );
}

function buildFileList(
  files: FileMap,
  rootFolder = '/',
  hideRoot: boolean,
  hiddenFiles: Array<string | RegExp>,
): Node[] {
  const fileList: Node[] = [];

  let defaultDepth = 0;

  if (rootFolder === '/' && !hideRoot) {
    defaultDepth = 1;
    fileList.push({ kind: 'folder', name: '/', depth: 0, id: 0, fullPath: '/' });
  }

  if (!rootFolder.endsWith('/')) {
    rootFolder += '/';
  }

  for (const [fullPath, dirent] of Object.entries(files)) {
    const segments = fullPath.slice(rootFolder.length).split('/');
    const name = segments.at(-1);
    const depth = segments.length - 1;

    if (!name || !dirent || isHiddenFile(fullPath, name, hiddenFiles)) {
      continue;
    }

    if (!fullPath.startsWith(rootFolder) || (hideRoot && fullPath === rootFolder)) {
      continue;
    }

    fileList.push({
      ...(dirent.type === 'file' ? { kind: dirent.type, dirent } : { kind: dirent.type, dirent }),
      id: fileList.length,
      name,
      fullPath,
      depth: depth + defaultDepth,
    });
  }

  return fileList.sort(compareFiles);
}

function isHiddenFile(filePath: string, fileName: string, hiddenFiles: Array<string | RegExp>) {
  return hiddenFiles.some((pathOrRegex) => {
    if (typeof pathOrRegex === 'string') {
      return fileName === pathOrRegex;
    }

    return pathOrRegex.test(filePath);
  });
}
